import React, { useEffect, useState } from "react";
import API from "../../utils/api";
// Inline toast — no external dependency needed
let _tRoot = null;
function _getToastRoot() {
  if (!_tRoot) {
    _tRoot = document.createElement("div");
    Object.assign(_tRoot.style, {
      position: "fixed", top: "20px", right: "20px",
      zIndex: "999999", display: "flex", flexDirection: "column", gap: "8px",
    });
    document.body.appendChild(_tRoot);
  }
  return _tRoot;
}
const toast = {
  _show(msg, bg) {
    const el = document.createElement("div");
    Object.assign(el.style, {
      background: bg, color: "#fff", padding: "10px 16px",
      borderRadius: "8px", fontSize: "13px", fontWeight: "600",
      boxShadow: "0 4px 14px rgba(0,0,0,0.25)", maxWidth: "320px",
      opacity: "1", transition: "opacity 0.4s",
    });
    el.textContent = msg;
    _getToastRoot().appendChild(el);
    setTimeout(() => { el.style.opacity = "0"; setTimeout(() => el.remove(), 400); }, 3000);
  },
  success(msg) { this._show(msg, "#10b981"); },
  error(msg)   { this._show(msg, "#ef4444"); },
  info(msg)    { this._show(msg, "#3b82f6"); },
};

// New status flow: pending → pending_manager_approval → pending_finance_approval → reimbursement_credited
// Legacy statuses normalised below
const normaliseStatus = s => {
  if (!s) return "pending";
  if (["submitted","under_review"].includes(s))    return "pending_manager_approval";
  if (["approved","payment_initiated"].includes(s)) return "pending_finance_approval";
  if (s === "paid")                                  return "reimbursement_credited";
  return s;
};

const STEPS = [
  { key: "pending_manager_approval", label: "Pending with Manager", icon: "🕐" },
  { key: "pending_finance_approval", label: "Pending with Finance",  icon: "🏦" },
  { key: "reimbursement_credited",   label: "Reimbursed",            icon: "💸" },
];

const STATUS_COLOR = {
  pending:                   { color: "#9ca3af", bg: "#f3f4f6"  },
  pending_manager_approval:  { color: "#92400e", bg: "#fef3c7"  },
  pending_finance_approval:  { color: "#1d4ed8", bg: "#dbeafe"  },
  reimbursement_credited:    { color: "#065f46", bg: "#d1fae5"  },
};

const STATUS_LABEL = {
  pending:                   "Not Submitted",
  pending_manager_approval:  "Pending with Manager",
  pending_finance_approval:  "Pending with Finance",
  reimbursement_credited:    "Reimbursed",
};

function StatusTimeline({ currentStatus }) {
  const normStatus = normaliseStatus(currentStatus);
  const currentIdx = STEPS.findIndex(s => s.key === normStatus);

  return (
    <div style={{ display: "flex", alignItems: "center", gap: 0, margin: "16px 0 8px", overflowX: "auto", paddingBottom: 4 }}>
      {STEPS.map((step, i) => {
        const done    = i <= currentIdx;
        const active  = i === currentIdx;

        return (
          <React.Fragment key={step.key}>
            <div style={{ display: "flex", flexDirection: "column", alignItems: "center", minWidth: 80 }}>
              <div style={{
                width: 36, height: 36, borderRadius: "50%",
                display: "flex", alignItems: "center", justifyContent: "center",
                fontSize: 16,
                background: done ? (active ? "#059669" : "#d1fae5") : "#f3f4f6",
                border: `2px solid ${done ? "#059669" : "#e5e7eb"}`,
                color: done ? "#065f46" : "#9ca3af",
                fontWeight: 700,
                transition: "all .2s",
              }}>
                {done ? step.icon : (i + 1)}
              </div>
              <div style={{
                fontSize: 10, fontWeight: active ? 700 : 500,
                color: active ? "#059669" : done ? "#6b7280" : "#9ca3af",
                marginTop: 4, textAlign: "center", lineHeight: 1.3,
                maxWidth: 74,
              }}>
                {step.label}
              </div>
            </div>
            {i < STEPS.length - 1 && (
              <div style={{
                flex: 1, height: 2, minWidth: 16,
                background: i < currentIdx ? "#059669" : "#e5e7eb",
                margin: "0 2px 20px",
              }} />
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
}

export default function MyReimbursements() {
  const [trips, setTrips]     = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter]   = useState("all"); // all | pending | in_progress | reimbursed
  const [submitting, setSubmitting] = useState(null); // tripId being submitted

  const reload = () => {
    setLoading(true);
    API.get("/reimbursements/my")
      .then(r => { setTrips(r.data); setLoading(false); })
      .catch(() => setLoading(false));
  };

  useEffect(reload, []);

  const handleSubmit = async (tripId) => {
    setSubmitting(tripId);
    try {
      const r = await API.post(`/reimbursements/trips/${tripId}/submit`);
      toast.success(r.data.message || "Reimbursement submitted!");
      reload();
    } catch (err) {
      toast.error(err.response?.data?.error || "Failed to submit reimbursement");
    } finally {
      setSubmitting(null);
    }
  };

  // Trips where excess exists but not yet submitted
  const needsSubmission = trips.filter(t =>
    t.reimbursement_status === "pending" &&
    parseFloat(t.total_expenses || 0) > parseFloat(t.advance_amount || 0)
  );

  const displayed = trips.filter(t => {
    const ns = normaliseStatus(t.reimbursement_status);
    if (filter === "pending")      return t.reimbursement_status === "pending" &&
                                          parseFloat(t.total_expenses || 0) > parseFloat(t.advance_amount || 0);
    if (filter === "in_progress")  return ns === "pending_manager_approval" || ns === "pending_finance_approval";
    if (filter === "reimbursed")   return ns === "reimbursement_credited";
    return true;
  });

  const totalPending = trips.filter(t => {
    const ns = normaliseStatus(t.reimbursement_status);
    return ns === "pending_manager_approval" || ns === "pending_finance_approval";
  }).reduce((s, t) => s + parseFloat(t.reimbursement_amount || 0), 0);

  const totalPaid = trips.filter(t => normaliseStatus(t.reimbursement_status) === "reimbursement_credited")
    .reduce((s, t) => s + parseFloat(t.reimbursement_amount || 0), 0);

  return (
    <div>
      <h1 className="page-title">💰 My Reimbursements</h1>
      <p className="page-subtitle" style={{ marginBottom: 24 }}>
        Track the payment status of all your settled trips
      </p>

      {/* Summary cards */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px,1fr))", gap: 16, marginBottom: 28 }}>
        {[
          { label: "Total Requests", value: trips.length, icon: "📋", color: "#1d4ed8", bg: "#dbeafe" },
          { label: "Awaiting Payment", value: `₹${totalPending.toLocaleString("en-IN")}`, icon: "⏳", color: "#92400e", bg: "#fef3c7" },
          { label: "Total Paid", value: `₹${totalPaid.toLocaleString("en-IN")}`, icon: "✅", color: "#065f46", bg: "#d1fae5" },
        ].map(c => (
          <div key={c.label} className="card" style={{ padding: "18px 20px", display: "flex", gap: 14, alignItems: "center" }}>
            <div style={{ fontSize: 28, background: c.bg, width: 52, height: 52, borderRadius: 12, display: "flex", alignItems: "center", justifyContent: "center" }}>{c.icon}</div>
            <div>
              <div style={{ fontSize: 11, color: "#6b7280", fontWeight: 600, textTransform: "uppercase", marginBottom: 2 }}>{c.label}</div>
              <div style={{ fontSize: 20, fontWeight: 800, color: c.color }}>{c.value}</div>
            </div>
          </div>
        ))}
      </div>

      {/* Needs Submission Banner */}
      {needsSubmission.length > 0 && (
        <div style={{ marginBottom: 20, padding: "14px 18px", background: "#FFF7ED", border: "2px solid #FDE68A", borderRadius: 12 }}>
          <div style={{ fontSize: 14, fontWeight: 700, color: "#D97706", marginBottom: 6 }}>
            💰 {needsSubmission.length} trip{needsSubmission.length > 1 ? "s" : ""} eligible for Reimbursement
          </div>
          <div style={{ fontSize: 13, color: "#92400e" }}>
            Your expenses exceeded the advance on {needsSubmission.length > 1 ? "these trips" : "this trip"}. Submit to start the approval process.
          </div>
        </div>
      )}

      {/* Filter tabs */}
      <div style={{ display: "flex", gap: 8, marginBottom: 20, flexWrap: "wrap" }}>
        {[
          ["all", "All Trips"],
          ["pending", `Needs Submission${needsSubmission.length ? ` (${needsSubmission.length})` : ""}`],
          ["in_progress", "In Progress"],
          ["reimbursed", "Reimbursed"],
        ].map(([key, lbl]) => (
          <button key={key} onClick={() => setFilter(key)}
            style={{
              padding: "7px 18px", borderRadius: 8, border: "1px solid",
              fontWeight: 600, fontSize: 13, cursor: "pointer",
              background: filter === key ? "#1d4ed8" : "white",
              color: filter === key ? "white" : "#374151",
              borderColor: filter === key ? "#1d4ed8" : "#d1d5db",
            }}>
            {lbl}
          </button>
        ))}
      </div>

      {loading ? (
        <div style={{ textAlign: "center", padding: 60, color: "#9ca3af" }}>Loading...</div>
      ) : displayed.length === 0 ? (
        <div className="card">
          <div className="empty-state">
            <div style={{ fontSize: 48 }}>💰</div>
            <h3>No reimbursements yet</h3>
            <p>Once your trip is settled and you request reimbursement, it will appear here.</p>
          </div>
        </div>
      ) : (
        <div style={{ display: "grid", gap: 20 }}>
          {displayed.map(trip => {
            const ns  = normaliseStatus(trip.reimbursement_status);
            const sc  = STATUS_COLOR[ns] || STATUS_COLOR.pending;
            const showTimeline = ns !== "pending";
            const excess = Math.max(0, parseFloat(trip.total_expenses||0) - parseFloat(trip.advance_amount||0));
            const canSubmit = trip.reimbursement_status === "pending" && excess > 0;

            return (
              <div key={trip.id} className="card" style={{ padding: 0, overflow: "hidden" }}>
                {/* Card header */}
                <div style={{ padding: "18px 24px", borderBottom: "1px solid #f3f4f6", display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: 12 }}>
                  <div>
                    <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 6, flexWrap: "wrap" }}>
                      <h3 style={{ fontSize: 16, fontWeight: 700, color: "#1f2937" }}>{trip.title}</h3>
                      <span style={{
                        fontSize: 12, fontWeight: 700, padding: "3px 10px", borderRadius: 20,
                        color: sc.color, background: sc.bg, border: `1px solid ${sc.color}33`
                      }}>
                        {STATUS_LABEL[ns] || ns}
                      </span>
                    </div>
                    <div style={{ fontSize: 13, color: "#6b7280" }}>
                      {trip.destination && <span>📍 {trip.destination} &nbsp;&bull;&nbsp; </span>}
                      📅 {new Date(trip.start_date).toLocaleDateString("en-IN")}
                      {trip.end_date && ` — ${new Date(trip.end_date).toLocaleDateString("en-IN")}`}
                    </div>
                  </div>
                  {/* Amounts */}
                  <div style={{ display: "flex", gap: 16, alignItems: "flex-start", flexWrap: "wrap" }}>
                    <div style={{ textAlign: "right" }}>
                      <div style={{ fontSize: 11, color: "#9ca3af", fontWeight: 600, marginBottom: 2 }}>TOTAL SPENT</div>
                      <div style={{ fontSize: 20, fontWeight: 800, color: "#E31E24" }}>₹{parseFloat(trip.total_expenses||0).toLocaleString("en-IN")}</div>
                    </div>
                    <div style={{ textAlign: "right" }}>
                      <div style={{ fontSize: 11, color: "#9ca3af", fontWeight: 600, marginBottom: 2 }}>ADVANCE</div>
                      <div style={{ fontSize: 20, fontWeight: 800, color: "#1d4ed8" }}>₹{parseFloat(trip.advance_amount||0).toLocaleString("en-IN")}</div>
                    </div>
                    {excess > 0 && (
                      <div style={{ textAlign: "right" }}>
                        <div style={{ fontSize: 11, color: "#9ca3af", fontWeight: 600, marginBottom: 2 }}>REIMBURSEMENT DUE</div>
                        <div style={{ fontSize: 20, fontWeight: 800, color: "#059669" }}>₹{excess.toLocaleString("en-IN")}</div>
                      </div>
                    )}
                    {trip.reimbursement_amount && !canSubmit && (
                      <div style={{ textAlign: "right" }}>
                        <div style={{ fontSize: 11, color: "#9ca3af", fontWeight: 600, marginBottom: 2 }}>REIMBURSING</div>
                        <div style={{ fontSize: 20, fontWeight: 800, color: "#059669" }}>₹{parseFloat(trip.reimbursement_amount).toLocaleString("en-IN")}</div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Submit for Reimbursement button */}
                {canSubmit && (
                  <div style={{ padding: "14px 24px", background: "#F0FDF4", borderBottom: "1px solid #D1FAE5" }}>
                    <div style={{ fontSize: 13, color: "#065f46", marginBottom: 10 }}>
                      💡 You spent <strong>₹{excess.toLocaleString("en-IN")} more</strong> than the advance. Submit this for reimbursement — it will go to your Manager for approval, then to Finance.
                    </div>
                    <button
                      onClick={() => handleSubmit(trip.id)}
                      disabled={submitting === trip.id}
                      style={{
                        padding: "9px 22px", borderRadius: 8, border: "none", cursor: "pointer",
                        background: "#059669", color: "white", fontWeight: 700, fontSize: 13,
                        opacity: submitting === trip.id ? 0.6 : 1,
                      }}>
                      {submitting === trip.id ? "Submitting…" : "💸 Submit for Reimbursement"}
                    </button>
                  </div>
                )}

                {/* Timeline */}
                {showTimeline && (
                  <div style={{ padding: "12px 24px 4px" }}>
                    <StatusTimeline currentStatus={trip.reimbursement_status} />
                  </div>
                )}

                {/* Payment details (shown once reimbursed) */}
                {(ns === "reimbursement_credited") && (
                  <div style={{ padding: "12px 24px 16px", display: "flex", gap: 24, flexWrap: "wrap" }}>
                    {trip.payment_reference && (
                      <div>
                        <div style={{ fontSize: 11, color: "#9ca3af", fontWeight: 600 }}>REFERENCE</div>
                        <div style={{ fontSize: 14, fontWeight: 700, color: "#1f2937" }}>💳 {trip.payment_reference}</div>
                      </div>
                    )}
                    {trip.reimbursement_date && (
                      <div>
                        <div style={{ fontSize: 11, color: "#9ca3af", fontWeight: 600 }}>PAYMENT DATE</div>
                        <div style={{ fontSize: 14, fontWeight: 700, color: "#1f2937" }}>
                          📅 {new Date(trip.reimbursement_date).toLocaleDateString("en-IN")}
                        </div>
                      </div>
                    )}
                    {trip.processed_by_name && (
                      <div>
                        <div style={{ fontSize: 11, color: "#9ca3af", fontWeight: 600 }}>PROCESSED BY</div>
                        <div style={{ fontSize: 14, fontWeight: 700, color: "#1f2937" }}>👤 {trip.processed_by_name}</div>
                      </div>
                    )}
                  </div>
                )}

                {/* Admin notes */}
                {trip.reimbursement_notes && (
                  <div style={{ padding: "8px 24px 16px" }}>
                    <div style={{ fontSize: 12, color: "#6b7280", padding: "10px 14px", background: "#f9fafb", borderRadius: 8, borderLeft: "3px solid #d1d5db" }}>
                      💬 <em>{trip.reimbursement_notes}</em>
                    </div>
                  </div>
                )}

                {/* Submitted timestamp */}
                {trip.reimbursement_submitted_at && (
                  <div style={{ padding: "0 24px 14px" }}>
                    <div style={{ fontSize: 11, color: "#9ca3af" }}>
                      Submitted {new Date(trip.reimbursement_submitted_at).toLocaleString("en-IN")}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
